/**
 * Tynn klient rundt fetch.
 *
 * Every call sends cookies (the session lives in an httpOnly cookie) and turns
 * the API's error envelope into an `ApiError`, so pages can react to a 402 by
 * showing an upgrade prompt instead of a generic failure.
 */

export type ApiErrorBody = {
  code: string;
  message: string;
  details?: { requiredPlan?: string; feature?: string; missing?: string[] };
  fields?: Array<{ path: string; message: string }>;
};

export class ApiError extends Error {
  status: number;
  code: string;
  details?: ApiErrorBody['details'];
  fields?: ApiErrorBody['fields'];

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.fields = body.fields;
  }

  /** Sant når planen må oppgraderes for å komme videre. */
  get isPlanRequired(): boolean {
    return this.status === 402;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** Feilmelding for et bestemt skjemafelt, om API-et pekte på ett. */
  fieldError(path: string): string | undefined {
    return this.fields?.find((field) => field.path === path)?.message;
  }
}

const BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Sendes på `window` når API-et sier at delingen er satt på pause.
 *
 * Pausen begynner når gratisperioden går ut, ikke når noen trykker på noe.
 * En toastmaster som har appen åpen akkurat da, har en økt som fortsatt viser
 * verktøyene – og hver side svarer med en feil. `AuthProvider` henter økta på
 * nytt når dette kommer, så pausen vises i stedet.
 */
export const DELING_PAA_PAUSE = 'vardag:deling-paa-pause';

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}/api${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, {
      code: 'network_error',
      message: 'Fikk ikke kontakt med serveren. Sjekk nettforbindelsen.',
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    const envelope = (payload as { error?: ApiErrorBody } | null)?.error;
    if (envelope?.code === 'sharing_paused' && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(DELING_PAA_PAUSE));
    }
    throw new ApiError(
      response.status,
      envelope ?? { code: 'unknown_error', message: 'Noe gikk galt. Prøv igjen.' },
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

/** Nedlasting av dataeksport (kommer som fil, ikke JSON i minnet). */
export async function downloadExport(): Promise<void> {
  const response = await fetch(`${BASE}/api/gdpr/export`, { credentials: 'include' });
  if (!response.ok) {
    throw new ApiError(response.status, {
      code: 'export_failed',
      message: 'Kunne ikke laste ned dataene dine.',
    });
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'vaardag-eksport.json';
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
